import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  MapPin, GraduationCap, Calendar, Clock, Users, ArrowLeft, Star, CheckCircle2, Building2, BookmarkPlus, Lock,
} from 'lucide-react';
import { useJsonLd, SITE_URL, SITE_LOGO } from './useJsonLd';
import Seo from '../../components/Seo';
import ShareListing from '../../components/ShareListing';

const InternshipDetail = () => {
  const { internshipId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [it, setIt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [savingTracker, setSavingTracker] = useState(false);

  useJsonLd('internship-jobposting-schema', it ? {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: it.title,
    description: it.description,
    datePosted: it.created_at ? new Date(it.created_at).toISOString().split('T')[0] : undefined,
    validThrough: it.expires_at ? new Date(it.expires_at).toISOString().split('T')[0] : undefined,
    employmentType: 'INTERN',
    industry: 'Social Services',
    occupationalCategory: '21-1021.00 - Child, Family, and School Social Workers',
    identifier: { '@type': 'PropertyValue', name: it.org_name, value: it.internship_id },
    hiringOrganization: { '@type': 'Organization', name: it.org_name, logo: SITE_LOGO },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: it.location ? it.location.split(',')[0]?.trim() : '',
        addressRegion: it.state || (it.location ? it.location.split(',')[1]?.trim() : ''),
        addressCountry: 'US',
      },
    },
    jobLocationType: it.format === 'remote' ? 'TELECOMMUTE' : undefined,
    applicantLocationRequirements: { '@type': 'Country', name: 'United States' },
    url: it.is_external && it.external_url ? it.external_url : `${SITE_URL}/internships/${it.internship_id}`,
    directApply: !it.is_external,
  } : null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/internships/${internshipId}`);
      setIt(res.data);
      document.title = `${res.data.title} — ${res.data.org_name} | Social Work Internship`;
      if (isAuthenticated) {
        try {
          const a = await axios.get(`${API_URL}/internships/${internshipId}/my-application`);
          if (a.data.application) setApplied(true);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      setIt(null);
    } finally {
      setLoading(false);
    }
  }, [internshipId, isAuthenticated]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApply = () => {
    if (!isAuthenticated) {
      toast.info('Create a free account to apply for this internship.');
      navigate('/register?type=job_seeker');
      return;
    }
    if (it.is_external && it.external_url) {
      window.open(it.external_url, '_blank', 'noopener');
      return;
    }
    setOpen(true);
  };

  const submitApply = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/internships/${internshipId}/apply`, { cover_note: note });
      toast.success(res.data.message || 'Application submitted!');
      setApplied(true);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit application.');
    } finally {
      setSending(false);
    }
  };

  const saveToTracker = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to save this internship to your private tracker.');
      navigate('/login');
      return;
    }
    setSavingTracker(true);
    try {
      const res = await axios.post(`${API_URL}/internship-portal/save-listing`, { internship_id: internshipId });
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save to tracker.');
    } finally {
      setSavingTracker(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20">Loading…</div>;
  if (!it) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-20 gap-3">
      <p className="text-gray-600">Internship not found.</p>
      <Link to="/internships"><Button variant="outline">Back to internships</Button></Link>
    </div>
  );

  if (it.is_expired) return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center pt-24 pb-16 px-4 text-center" data-testid="internship-expired">
      <Seo
        title="Internship No Longer Available"
        description="This social work internship listing is no longer available. Browse current field placements on I Need A Social Worker."
        noindex
      />
      <h1 className="font-serif text-2xl font-bold text-gray-900">This internship is no longer available</h1>
      <p className="text-gray-600 mt-2 max-w-md">
        {it.title ? `“${it.title}” has been filled or expired. ` : ''}Explore current social work internships instead.
      </p>
      <Link to="/internships" className="mt-5"><Button>Browse active internships</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/internships" className="inline-flex items-center gap-1 text-primary hover:text-secondary text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to internships
        </Link>

        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="bg-primary p-6 sm:p-8 text-white">
            {it.is_featured && <Badge className="bg-secondary text-white mb-2 flex items-center gap-1 w-fit"><Star className="w-3.5 h-3.5" /> Featured</Badge>}
            <h1 className="font-serif text-2xl sm:text-3xl font-bold" data-testid="internship-title">{it.title}</h1>
            <p className="text-white/80 flex items-center gap-2 mt-2"><Building2 className="w-4 h-4" /> {it.org_name}</p>
            <p className="text-white/80 flex flex-wrap items-center gap-3 mt-2 text-sm">
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {it.location}</span>
              <span className="capitalize">{it.format?.replace('_', ' ')}</span>
            </p>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{it.degree_level}</Badge>
              <Badge variant="outline">{it.concentration}</Badge>
              {it.supervision_included && <Badge className="bg-green-100 text-green-800">Supervision Included</Badge>}
            </div>
            <ShareListing url={`${window.location.origin}/internships/${internshipId}`} title={`${it.title} — ${it.org_name}`} />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              {it.start_date && <Info icon={Calendar} label="Start Date" value={it.start_date} />}
              {it.weekly_hours && <Info icon={Clock} label="Weekly Hours" value={it.weekly_hours} />}
              {it.semester && <Info icon={Calendar} label="Semester" value={it.semester} />}
              {it.population_served && <Info icon={Users} label="Population" value={it.population_served} />}
            </div>

            <div>
              <h2 className="font-serif text-lg font-bold text-primary mb-2">Description</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{it.description}</p>
            </div>

            {it.application_deadline && (
              <p className="text-sm text-gray-600"><strong>Application deadline:</strong> {it.application_deadline}</p>
            )}

            {/* Contact & application details — gated behind Internship Portal membership */}
            <div className="border-t pt-6">
              <h2 className="font-serif text-lg font-bold text-primary mb-2">Contact & Application Details</h2>
              {it.membership_required ? (
                <div className="bg-secondary/10 border border-secondary/30 rounded-xl p-5" data-testid="contact-locked-notice">
                  <p className="flex items-center gap-2 text-primary font-semibold"><Lock className="w-4 h-4 text-secondary" /> Contact info is members-only</p>
                  <p className="text-sm text-gray-600 mt-1">Activate an Internship Portal Membership to view internship contact information, application details, and tracking tools.</p>
                  <Link to="/internship-portal/membership">
                    <Button className="mt-3 bg-secondary hover:bg-secondary-hover text-white" data-testid="view-membership-options-btn">View Membership Options</Button>
                  </Link>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-5 text-sm text-gray-700 space-y-1" data-testid="contact-unlocked">
                  {it.contact_email && <p><strong>Email:</strong> <a className="text-primary hover:underline" href={`mailto:${it.contact_email}`}>{it.contact_email}</a></p>}
                  {it.contact_phone && <p><strong>Phone:</strong> {it.contact_phone}</p>}
                  {it.contact_name && <p><strong>Contact:</strong> {it.contact_name}</p>}
                  {it.full_address && <p><strong>Address:</strong> {it.full_address}</p>}
                  {(it.org_website || it.website) && <p><strong>Website:</strong> <a className="text-primary hover:underline" href={it.org_website || it.website} target="_blank" rel="noreferrer">{it.org_website || it.website}</a></p>}
                  {(it.application_link || it.apply_url) && <p><strong>Apply:</strong> <a className="text-primary hover:underline" href={it.application_link || it.apply_url} target="_blank" rel="noreferrer">Application link</a></p>}
                  {it.application_instructions && <p><strong>Instructions:</strong> {it.application_instructions}</p>}
                  {!it.contact_email && !it.contact_phone && !it.full_address && <p className="text-gray-500">No additional contact details were provided for this listing.</p>}
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <div className="flex flex-wrap items-center gap-3">
                {applied ? (
                  <div className="flex items-center gap-2 text-green-700 font-semibold" data-testid="already-applied">
                    <CheckCircle2 className="w-5 h-5" /> You've applied to this internship
                  </div>
                ) : (
                  <Button className="bg-secondary hover:bg-secondary-hover text-white" onClick={handleApply} data-testid="apply-internship-btn">
                    <GraduationCap className="w-4 h-4 mr-2" /> {it.is_external ? 'Apply on Original Site' : 'Apply for this Internship'}
                  </Button>
                )}
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" onClick={saveToTracker} disabled={savingTracker} data-testid="save-to-tracker-btn">
                  <BookmarkPlus className="w-4 h-4 mr-2" /> {savingTracker ? 'Saving…' : 'Save to My Internship Tracker'}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                {it.is_external
                  ? 'This listing is aggregated from a public job source. Create a free account, then continue to the original posting to apply.'
                  : 'You must create a free account to apply. Your details are shared with the posting organization.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Apply — {it.title}</DialogTitle></DialogHeader>
          <Textarea placeholder="Add a short note for the organization (optional)…" value={note}
            onChange={(e) => setNote(e.target.value)} rows={5} data-testid="apply-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-secondary text-white" onClick={submitApply} disabled={sending} data-testid="submit-apply-btn">
              {sending ? 'Submitting…' : 'Submit Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-xs text-gray-400 flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

export default InternshipDetail;
